// EMP501 — bi-annual reconciliation submitted to SARS. Interim period covers
// March–August (filed by 31 October), annual covers March–February (filed by
// 31 May). Rolls up every payroll_run + payslip per employee into an
// IRP5/IT3a-equivalent line.
import { useEffect, useMemo, useState } from 'react';
import { FileText, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { formatMoney } from '../lib/format';

type Period = 'interim' | 'annual';

function periodBounds(year: number, p: Period): { from: { y: number; m: number }; to: { y: number; m: number } } {
  // SA tax year: 1 March → end Feb the following year.
  if (p === 'interim') return { from: { y: year, m: 3 }, to: { y: year, m: 8 } };
  return { from: { y: year, m: 3 }, to: { y: year + 1, m: 2 } };
}

export default function EMP501() {
  const { currentOrg } = useOrg();
  const today = new Date();
  const taxYear = today.getMonth() + 1 >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const [year, setYear] = useState(taxYear);
  const [period, setPeriod] = useState<Period>('interim');
  const [runs, setRuns] = useState<any[]>([]);
  const [slips, setSlips] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);

  async function load() {
    if (!currentOrg) return;
    const { from, to } = periodBounds(year, period);
    const fromKey = from.y * 100 + from.m;
    const toKey = to.y * 100 + to.m;
    const { data: r } = await supabase.from('dodobooks_payroll_runs').select('*').eq('org_id', currentOrg.id);
    const inRange = (r ?? []).filter((x: any) => {
      const k = x.period_year * 100 + x.period_month;
      return k >= fromKey && k <= toKey;
    });
    setRuns(inRange);
    if (inRange.length) {
      const ids = inRange.map((x: any) => x.id);
      const { data: ps } = await supabase.from('dodobooks_payslips').select('*').in('payroll_run_id', ids);
      setSlips(ps ?? []);
      const empIds = Array.from(new Set((ps ?? []).map((p: any) => p.employee_id)));
      if (empIds.length) {
        const { data: es } = await supabase.from('dodobooks_employees').select('*').in('id', empIds);
        setEmployees(es ?? []);
      } else { setEmployees([]); }
    } else {
      setSlips([]); setEmployees([]);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentOrg, year, period]);

  const rollup = useMemo(() => {
    const byEmp: Record<string, { gross: number; paye: number; uif: number; sdl: number }> = {};
    for (const s of slips) {
      const k = s.employee_id;
      if (!byEmp[k]) byEmp[k] = { gross: 0, paye: 0, uif: 0, sdl: 0 };
      byEmp[k].gross += Number(s.gross);
      byEmp[k].paye += Number(s.paye);
      byEmp[k].uif  += Number(s.uif_employee) + Number(s.uif_employer);
      byEmp[k].sdl  += Number(s.sdl);
    }
    return byEmp;
  }, [slips]);

  const totals = useMemo(() => {
    let g = 0, p = 0, u = 0, s = 0;
    for (const v of Object.values(rollup)) { g += v.gross; p += v.paye; u += v.uif; s += v.sdl; }
    return { gross: g, paye: p, uif: u, sdl: s };
  }, [rollup]);

  function exportCsv() {
    const header = ['Employee', 'ID number', 'Tax number', 'Gross', 'PAYE', 'UIF', 'SDL'];
    const rows = employees.map((e) => {
      const r = rollup[e.id] ?? { gross: 0, paye: 0, uif: 0, sdl: 0 };
      return [e.full_name, e.id_number ?? '', e.tax_number ?? '', r.gross.toFixed(2), r.paye.toFixed(2), r.uif.toFixed(2), r.sdl.toFixed(2)];
    });
    const csv = [header, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `EMP501-${year}-${period}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  }

  const periodLabel = period === 'interim'
    ? `Mar ${year} → Aug ${year} (interim — due 31 Oct)`
    : `Mar ${year} → Feb ${year + 1} (annual — due 31 May)`;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="h-page flex items-center gap-2"><FileText size={22} /> EMP501</h1>
        <p className="text-sm text-text-mute mt-1">Bi-annual employer reconciliation. Rolls up every monthly EMP201 into IRP5/IT3a totals per employee.</p>
      </div>

      <div className="card flex flex-wrap gap-3 items-end">
        <div className="field !mb-0"><label>Tax year start</label><input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} /></div>
        <div className="field !mb-0"><label>Period</label>
          <select value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
            <option value="interim">Interim (Mar–Aug)</option>
            <option value="annual">Annual (Mar–Feb)</option>
          </select>
        </div>
        <div className="text-xs text-text-mute mb-1">Covering: {periodLabel}</div>
        <button onClick={exportCsv} className="btn btn-secondary ml-auto"><Download size={14} /> Export CSV</button>
      </div>

      <div className="card overflow-x-auto">
        <h2 className="h-section text-base mb-3">Per-employee totals</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-text-mute uppercase tracking-widest font-mono">
              <th className="px-3 py-2">Employee</th>
              <th className="px-3 py-2">ID / Tax</th>
              <th className="px-3 py-2 text-right">Gross</th>
              <th className="px-3 py-2 text-right">PAYE</th>
              <th className="px-3 py-2 text-right">UIF</th>
              <th className="px-3 py-2 text-right">SDL</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((e) => {
              const r = rollup[e.id] ?? { gross: 0, paye: 0, uif: 0, sdl: 0 };
              return (
                <tr key={e.id} className="border-t border-line">
                  <td className="px-3 py-2 font-semibold">{e.full_name}</td>
                  <td className="px-3 py-2 text-xs font-mono">{e.id_number ?? '—'}<br/>{e.tax_number ?? ''}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMoney(r.gross, 'ZAR')}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMoney(r.paye, 'ZAR')}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMoney(r.uif, 'ZAR')}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatMoney(r.sdl, 'ZAR')}</td>
                </tr>
              );
            })}
            {employees.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-text-mute text-sm">No payslips in this period yet.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-line font-bold">
              <td className="px-3 py-2" colSpan={2}>Totals · {runs.length} run{runs.length === 1 ? '' : 's'}</td>
              <td className="px-3 py-2 text-right font-mono">{formatMoney(totals.gross, 'ZAR')}</td>
              <td className="px-3 py-2 text-right font-mono">{formatMoney(totals.paye, 'ZAR')}</td>
              <td className="px-3 py-2 text-right font-mono">{formatMoney(totals.uif, 'ZAR')}</td>
              <td className="px-3 py-2 text-right font-mono">{formatMoney(totals.sdl, 'ZAR')}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
